import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactVideoEditor } from '@reactvideoeditor/react-video-editor/react-video-editor';
import '@reactvideoeditor/react-video-editor/styles.css';
import type {
  Adaptors,
  EditorState,
} from '@reactvideoeditor/react-video-editor/types';
import type { CreativeEditorBackTarget } from '@/rve/editorBackNav';
import { outputResolutionFromNeoInputProps } from '@/rve/exportResolution';
import { i18nGoogleFontsStylesheetHref } from '@/rve/i18nFontFallbacks';
import { NeoEditorTopBarBack } from '@/rve/neoEditorTopBarBack';
import { NeoEditorDurationOverride } from '@/rve/neoEditorDurationOverride';
import { resetNeoPreviewRenderMode } from '@/rve/neoPreviewMode';
import { suppressRveStickerPreviewLogs } from '@/rve/suppressRveStickerPreviewLogs';

/** RVE defaults to 1s; SDK does not expose pollingInterval via customRenderer props. */
const PROGRESS_POLL_INTERVAL_MS = 15_000;

const ASPECTLY_SIDEBAR_LOGO = (
  <img
    src="/favicon.svg"
    alt="Aspectly"
    className="aspectly:h-8 aspectly:w-8 aspectly:object-contain"
  />
);

export interface EditorNeoShellProps {
  projectId: string;
  adaptors: Adaptors;
  editorState?: EditorState;
  onPersist: (state: EditorState) => Promise<void>;
  backTo: CreativeEditorBackTarget;
  durationMs?: number;
  onDurationChange?: (ms: number) => void;
}

function orgCreativeFromProjectId(projectId: string): { orgId: string; creativeId: string } {
  const parts = projectId.split('-');
  return { orgId: parts[2]!, creativeId: parts[4]! };
}

/** Full-viewport RVE SDK shell with Aspectly whitelabel and autosave persistence. */
export function EditorNeoShell({
  projectId,
  adaptors,
  editorState,
  onPersist,
  backTo,
  durationMs,
  onDurationChange,
}: EditorNeoShellProps) {
  const navigate = useNavigate();
  const shellRef = useRef<HTMLDivElement>(null);
  const lastProgressPollAt = useRef(0);

  useEffect(() => {
    resetNeoPreviewRenderMode();
    const fontLinkId = 'aspectly-i18n-fonts';
    if (!document.getElementById(fontLinkId)) {
      const link = document.createElement('link');
      link.id = fontLinkId;
      link.rel = 'stylesheet';
      link.href = i18nGoogleFontsStylesheetHref();
      document.head.appendChild(link);
    }
    return suppressRveStickerPreviewLogs();
  }, []);

  const handleSave = useCallback(
    (state: EditorState) => {
      void onPersist(state);
    },
    [onPersist],
  );

  return (
    <div ref={shellRef} className="aspectly:fixed aspectly:inset-0 aspectly:z-50 aspectly:bg-black">
      <NeoEditorTopBarBack
        containerRef={shellRef}
        backTo={backTo}
        onBack={() => navigate(backTo.path)}
      />
      <NeoEditorDurationOverride
        containerRef={shellRef}
        durationMs={durationMs}
        onDurationChange={onDurationChange}
      />
      <ReactVideoEditor
        key={durationMs}
        projectId={projectId}
        adaptors={adaptors}
        editorState={editorState}
        onSave={handleSave}
        autoSaveInterval={30_000}
        defaultTheme="dark"
        hideThemeToggle
        sidebarLogo={ASPECTLY_SIDEBAR_LOGO}
        sidebarFooterText="Aspectly"
        sidebarWidth="clamp(350px, 25vw, 500px)"
        sidebarIconWidth="57.6px"
        showIconTitles={false}
        fps={30}
        exportOptions={{
          availableResolutions: ['720p', '1080p', '4k'],
          defaultResolution: '1080p',
        }}
        customRenderer={{
          renderType: { type: 'ssr', entryPoint: 'placeholder' },
          enabled: true,
          name: 'Aspectly Cloud Render',
          async renderVideo(params) {
            // Trigger a save of the creative with the latest state
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const inputProps = params.inputProps as any;
            if (inputProps?.editorState) {
              await onPersist(inputProps.editorState as EditorState);
            } else if (editorState) {
              await onPersist(editorState);
            }

            // Keep projectId stable for IndexedDB; mint a unique id per cloud render run.
            const { orgId, creativeId } = orgCreativeFromProjectId(projectId);
            const renderId = crypto.randomUUID();
            const outputResolution = outputResolutionFromNeoInputProps(inputProps ?? {});
            const endpoint = `/api/v1/organizations/${orgId}/creatives/${creativeId}/rve-render/render`;

            const res = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: renderId,
                creativeId,
                orgId,
                output_resolution: outputResolution,
              }),
            });
            if (!res.ok) {
              const errText = await res.text();
              throw new Error(`Failed to start render: ${errText}`);
            }
            const body = (await res.json()) as { renderId?: string; type?: string; progress?: number };
            lastProgressPollAt.current = 0;
            return {
              ...body,
              renderId: body.renderId ?? renderId,
            };
          },
          async getProgress(params) {
            // SDK hard-defaults to ~1s polls and does not pass pollingInterval through
            // customRenderer; pace client-side so we hit the API ~every 15s.
            const elapsed = Date.now() - lastProgressPollAt.current;
            if (lastProgressPollAt.current > 0 && elapsed < PROGRESS_POLL_INTERVAL_MS) {
              await new Promise((r) =>
                setTimeout(r, PROGRESS_POLL_INTERVAL_MS - elapsed),
              );
            }
            lastProgressPollAt.current = Date.now();

            const { orgId, creativeId } = orgCreativeFromProjectId(projectId);
            const endpoint = `/api/v1/organizations/${orgId}/creatives/${creativeId}/rve-render/progress`;

            const res = await fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: params.id }),
            });
            if (!res.ok) {
              const errText = await res.text();
              throw new Error(`Failed to check progress: ${errText}`);
            }
            return res.json();
          },
        }}
      />
    </div>
  );
}
